import React, { useRef, useEffect, useCallback, useContext, useState } from "react";
import { scaleLinear, scaleTime } from "d3-scale";
import { isEqual, isWithinInterval, Interval } from "date-fns";
import { timeFormat } from "d3-time-format";
import { extent } from "d3-array";
import { stack } from "d3-shape";
import { timeDay } from "d3-time";
import { useTheme } from "styled-components";
import { ITheme } from "@evergis/ui";

import Bar from "./Bar";
import AxisTooltip from "./Tooltip";
import { DateFilterContext } from "../../context";
import { useAxisHandler } from "../../hooks";
import { getTick, getTicks } from "../../uitls";
import { AxisContainer, TickLabel, Tick } from "../../styled";
import { Layer } from "../../reducers";
import { getKeyData } from "./utils";
import {
  LIGHT_OPACTIY,
  DEEP_OPACTIY,
  GRID_STEP,
  LABEL_SIZE,
  TICK_HEIGHT,
  SELECTED_TICK_HEIGHT,
  VIEPORT_TICK_OFFSET,
  RIGHT_PADDING,
} from "./constants";

export const hasAtrribteLayer = (layers: Layer[]): boolean =>
  layers.some(({ isAggregated, selected }) => isAggregated && selected);

const getHistogramLayers = (layers: Layer[]): Layer[] =>
  layers.filter(({ isAggregated, selected }) => isAggregated && selected);

const getTickFormat = (duration: Duration): string => {
  if (duration.years) {
    return "%Y";
  }
  if (duration.months) {
    return "%B";
  }
  if (duration.days) {
    return "%d %b";
  }
  if (duration.hours) {
    return "%d %b %H:%M";
  }
  if (duration.minutes) {
    return "%I:%M";
  }

  return "%M:%S";
};

type DateTicks = {
  ticks: { date: Date; x: number; label: string; selected: boolean; opacity: number }[];
  labelTicks: { datt: Date; x: number; label: string }[];
  visibleInterval: Interval;
};
const useTicks = (): DateTicks => {
  const [{ labelTicks, ticks, visibleInterval }, setTicks] = useState({
    labelTicks: [],
    ticks: [],
    visibleInterval: { start: new Date(), end: new Date() },
  });
  const {
    axisSize,
    scrollLeft,
    commonScale,
    selectedTick,
    durationInterval,
    rangeDate,
    tickWidth,
    commonTickScale,
    bandScale,
    layers,
  } = useContext(DateFilterContext);

  useEffect(() => {
    const start = scrollLeft;
    const end = axisSize.width + scrollLeft;
    const domain = [commonScale.invert(start), commonScale.invert(end)] as [Date, Date];
    const tickDomain = [
      Math.floor(commonTickScale(start)) - VIEPORT_TICK_OFFSET,
      Math.ceil(commonTickScale(end)) + VIEPORT_TICK_OFFSET,
    ];

    const timeScale = scaleTime().range([start, end]).domain(domain);
    const startDomain = getTick(rangeDate, durationInterval, tickDomain[0]);
    const endDomain = getTick(rangeDate, durationInterval, tickDomain[1]);
    const newTicks = getTicks([startDomain, endDomain], durationInterval);
    const lTicks = timeScale.nice(timeDay).ticks(Math.min(newTicks.length, 10));
    const labelFormatter = timeFormat(getTickFormat(durationInterval));

    bandScale.domain(newTicks).range([start, end]);

    setTicks({
      visibleInterval: { start: domain[0], end: domain[1] },
      labelTicks: lTicks.map(date => ({ date, x: commonScale(date), label: labelFormatter(date) })),
      ticks: newTicks.map(date => ({
        date,
        x: commonTickScale.invert(Math.round(commonTickScale(commonScale(date)))),
        label: labelFormatter(date),
        selected: selectedTick && selectedTick.getTime() === date.getTime(),
        opacity: Math.round(commonScale(date) / tickWidth / GRID_STEP) % 2 === 0 ? LIGHT_OPACTIY : DEEP_OPACTIY,
      })),
    });
  }, [
    axisSize.width,
    bandScale,
    commonScale,
    commonTickScale,
    durationInterval,
    layers,
    rangeDate,
    scrollLeft,
    selectedTick,
    tickWidth,
  ]);

  return { labelTicks, ticks, visibleInterval };
};

type HistogramData = { [key: string]: number } & { date: Date };

const useChangeSize = (setAxisSize, ref) => {
  const getSize = useCallback(() => {
    if (ref && ref.current) {
      setAxisSize({ width: ref.current.clientWidth, height: ref.current.clientHeight });
    }
  }, [ref, setAxisSize]);

  useEffect(() => {
    getSize();
    window.addEventListener("resize", getSize);

    return () => window.removeEventListener("resize", getSize);
  }, [getSize]);
};

export default () => {
  const theme = useTheme() as ITheme;
  const ref = useRef<HTMLDivElement>(null);
  const { setAxisSize, setScrollLeft, commonScale, tickWidth, layers } = useContext(DateFilterContext);
  const handler = useAxisHandler();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [, right] = commonScale.range();
  const withData = hasAtrribteLayer(layers);
  const [histogramData, setHistogramData] = useState<HistogramData[]>([]);

  useChangeSize(setAxisSize, ref);

  const { labelTicks, ticks, visibleInterval } = useTicks();
  const dataTicks = ticks.map(
    ({ date }) =>
      ({
        date,
        ...histogramData.find(({ date: histogramDate }) => isEqual(histogramDate, date)),
      } as HistogramData),
  );

  const selectedTickData = ticks.find(({ selected }) => selected);
  const svgWidth = right + tickWidth + RIGHT_PADDING;
  const svgHeight = withData ? 74 : 48;

  const extentDomain = useCallback((argument: Date, data: { key: string; value: number }) => {
    setHistogramData(state => {
      if (state.find(({ date }) => isEqual(date, argument))) {
        return state.map(item => {
          if (isEqual(item.date, argument)) {
            return { ...item, [data.key]: data.value };
          }

          return item;
        });
      }

      return [...state, { date: argument, [data.key]: data.value } as HistogramData];
    });
  }, []);

  const histogramLayers = getHistogramLayers(layers);
  const chartStack = stack().keys(histogramLayers.map(layer => getKeyData(layer)));
  const stackedDataTicks = chartStack(dataTicks);
  const stackedVisibleTicks = chartStack(dataTicks.filter(({ date }) => isWithinInterval(date, visibleInterval)));

  const domain = [0, extent(stackedVisibleTicks.flat(2))[1] as unknown as number];

  useEffect(() => {
    setHistogramData([]);
  }, [layers]);

  const yScale = scaleLinear().range([0, 26]).domain(domain);

  return (
    <AxisContainer
      ref={ref}
      onScroll={e => {
        setScrollLeft((e.target as HTMLDivElement).scrollLeft);
      }}
    >
      <svg
        onPointerDown={e => {
          if (ref && ref.current) {
            const rect = ref.current.getBoundingClientRect();

            handler(rect.left, e);
          }
          e.stopPropagation();
        }}
        width={isFinite(svgWidth) ? svgWidth : "100%"}
        height={svgHeight}
      >
        {selectedTickData && <AxisTooltip data={selectedTickData} svgHeight={svgHeight} withData={withData} />}
        {ticks.map(({ x, selected, opacity, date }, index) => (
          <g key={date.toString()}>
            <Tick
              rx={1}
              ry={1}
              fill={selected ? theme.palette.primary : theme.palette.textPrimary}
              opacity={selected ? 1 : opacity}
              y={0}
              transform={`translate(0, ${svgHeight - 24 - (selected ? SELECTED_TICK_HEIGHT : TICK_HEIGHT) / 2})`}
              x={x + 1}
              width={tickWidth - 1}
              height={selected ? SELECTED_TICK_HEIGHT : TICK_HEIGHT}
            />
            {histogramLayers.map((layer, layerIndex) => (
              <Bar
                key={date.toString() + layerIndex}
                date={date}
                x={x}
                extentDomain={extentDomain}
                yScale={yScale}
                layer={layer}
                data={stackedDataTicks[layerIndex][index] as [number, number]}
              />
            ))}
          </g>
        ))}
        {labelTicks
          .filter(({ x }) => x)
          .map(({ label, x }, index) => (
            <TickLabel key={x || index} x={x + tickWidth / 2} y={svgHeight - LABEL_SIZE} textAnchor="middle">
              {label}
            </TickLabel>
          ))}
      </svg>
    </AxisContainer>
  );
};
